import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  HostListener,
  AfterViewInit,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-floating-panel',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './floating-panel.component.html',
  styleUrls: ['./floating-panel.component.css'],
})
export class FloatingPanelComponent implements OnInit, AfterViewInit {
  @Input() title: string = 'Panel';
  @Input() width: string = '400px';
  @Input() height: string = '300px';
  @Input() minWidth: string = '200px';
  @Input() minHeight: string = '100px';
  @Input() margin: number = 20; // Margin from screen edges
  @Input() initialPosition: 'bottom-right' | 'custom' = 'bottom-right';
  @Input() customX?: number;
  @Input() customY?: number;

  @Output() closed = new EventEmitter<void>();
  @Output() minimizedChange = new EventEmitter<boolean>();

  @ViewChild('panel') panelElement!: ElementRef;
  @ViewChild('header') headerElement!: ElementRef;
  @ViewChild('content') contentElement!: ElementRef;

  panelVisible: boolean = true;
  isPanelMinimized: boolean = false;

  // Position tracking variables
  isDragging: boolean = false;
  currentX: number = 0;
  currentY: number = 0;
  expandedY: number = 0; // Store Y position when expanded
  initialMouseX: number = 0;
  initialMouseY: number = 0;

  // Dimensions
  headerHeight: number = 36; // Will be measured after init

  // Z-index management for multiple panels
  zIndex: number = 1000;
  private static highestZIndex: number = 1000;

  constructor() {}

  ngOnInit(): void {
    // Initial setup
  }

  ngAfterViewInit(): void {
    // Position panel after it's rendered and measurements are available
    setTimeout(() => {
      this.measureElements();
      this.positionPanelInitially();
    }, 0);
  }

  measureElements(): void {
    if (this.headerElement) {
      this.headerHeight = this.headerElement.nativeElement.offsetHeight;
    }
  }

  positionPanelInitially(): void {
    if (this.initialPosition === 'bottom-right') {
      this.positionAtBottomRight();
    } else if (this.customX !== undefined && this.customY !== undefined) {
      this.currentX = this.customX;
      this.currentY = this.customY;
    }

    this.expandedY = this.currentY; // Store initial Y position
    this.updatePanelPosition();
    this.constrainToViewport();
  }

  positionAtBottomRight(): void {
    if (!this.panelElement) return;

    // Get panel dimensions
    const panelWidth = this.panelElement.nativeElement.offsetWidth;
    const panelHeight = this.panelElement.nativeElement.offsetHeight;

    // Calculate position at bottom-right with margin
    this.currentX = window.innerWidth - panelWidth - this.margin;
    this.currentY = window.innerHeight - panelHeight - this.margin;
  }

  bringToFront(): void {
    FloatingPanelComponent.highestZIndex += 1;
    this.zIndex = FloatingPanelComponent.highestZIndex;
  }

  updatePanelPosition(): void {
    if (this.panelElement && this.panelElement.nativeElement) {
      this.panelElement.nativeElement.style.left = `${this.currentX}px`;
      this.panelElement.nativeElement.style.top = `${this.currentY}px`;
    }
  }

  constrainToViewport(): void {
    if (!this.panelElement) return;
    const panelWidth = this.panelElement.nativeElement.offsetWidth;
    const panelHeight = this.isPanelMinimized
      ? this.headerElement.nativeElement.offsetHeight
      : this.panelElement.nativeElement.offsetHeight;

    const margin = 20;
    const maxX = window.innerWidth - panelWidth - margin;
    const maxY = window.innerHeight - panelHeight - margin;

    // Apply constraints
    this.currentX = Math.max(margin, Math.min(this.currentX, maxX));
    this.currentY = Math.max(margin, Math.min(this.currentY, maxY));

    this.updatePanelPosition();

    this.updatePanelPosition();
  }

  onHeaderMouseDown(event: MouseEvent): void {
    // Only drag with primary mouse button (left click)
    if (event.button !== 0) return;

    this.bringToFront();
    this.isDragging = true;

    this.initialMouseX = event.clientX;
    this.initialMouseY = event.clientY;

    // Prevent text selection during drag
    event.preventDefault();
  }

  @HostListener('window:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (!this.isDragging) return;

    const dx = event.clientX - this.initialMouseX;
    const dy = event.clientY - this.initialMouseY;

    // IMPORTANT: Add these lines to constrain panel within viewport
    const panelWidth = this.panelElement.nativeElement.offsetWidth;
    const panelHeight = this.isPanelMinimized
      ? this.headerElement.nativeElement.offsetHeight
      : this.panelElement.nativeElement.offsetHeight;

    this.currentX += dx;
    this.currentY += dy;

    if (!this.isPanelMinimized) {
      this.expandedY = this.currentY; // Update stored expanded position
    }

    this.initialMouseX = event.clientX;
    this.initialMouseY = event.clientY;

    this.updatePanelPosition();
    this.constrainToViewport();
  }

  @HostListener('window:mouseup')
  onMouseUp(): void {
    this.isDragging = false;
  }

  @HostListener('window:resize')
  onResize(): void {
    // When the window resizes, make sure panel stays in view
    this.constrainToViewport();
  }

  toggleMinimize(): void {
    if (this.isPanelMinimized) {
      // Expanding - restore previous position
      this.isPanelMinimized = false;
      this.currentY = this.expandedY;
    } else {
      // Minimizing - move to bottom of screen
      this.isPanelMinimized = true;
      this.currentY = window.innerHeight - this.headerHeight - this.margin;
    }

    this.updatePanelPosition();
    this.minimizedChange.emit(this.isPanelMinimized);
  }

  closePanel(): void {
    // this.panelVisible = false;
    this.closed.emit();
  }

  showPanel(): void {
    this.panelVisible = true;
    this.bringToFront();
  }
}
